import styled from '@emotion/styled';
import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type To } from 'react-router';

import RouterLink from 'app/components/shared/RouterLink';
import Fade from 'app/components/v2/animations/Fade';
import Box from 'app/components/v2/components/Box';
import Pill, { type PillProps } from 'app/components/v2/components/Pill';
import Heading4 from 'app/components/v2/components/Text/Heading4';
import Heading5 from 'app/components/v2/components/Text/Heading5';
import Legal from 'app/components/v2/components/Text/Legal';
import SmallBody from 'app/components/v2/components/Text/SmallBody';

import { globalSearchInputMaxWidth, globalSearchTransitionSpeedMs } from '../constants';
import GlobalSearchDropdown from '../GlobalSearchDropdown';

interface GlobalSearchData {
  bottomLeft: string;
  id: string;
  pillProps: PillProps;
  to: To;
  topLeft: string;
  topRight: ReactNode;
}

export interface GlobalSearchDataSection {
  data: GlobalSearchData[];
  id: string;
  label: ReactNode;
  to?: To;
}

export interface GlobalSearchResultsProps {
  data: GlobalSearchDataSection[] | null;
}

interface Props extends GlobalSearchResultsProps {
  formElement: HTMLFormElement | null;
  isOpen: boolean;
  onClose: () => void;
}

const LabelContainer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.semanticColors.background_product,
  borderBottom: `1px solid ${theme.semanticColors.card_border}`,
  borderTop: `1px solid ${theme.semanticColors.card_border}`,
  display: 'flex',
  height: 45,
  justifyContent: 'space-between',
  paddingLeft: theme.spacing * 4,
  paddingRight: theme.spacing * 4,
}));

const ResultsContainer = styled(Box)({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '& > :first-of-type .label-container': {
    borderTop: 'none',
  },
});

const LinkSection = styled(RouterLink)(({ theme }) => ({
  backgroundColor: theme.semanticColors.card_background,
  borderBottom: `1px solid ${theme.semanticColors.card_border}`,
  display: 'block',
  padding: theme.spacing * 4,
  textDecoration: 'none',
  transition: `background-color ${globalSearchTransitionSpeedMs}ms`,

  // eslint-disable-next-line sort-keys, @typescript-eslint/naming-convention
  '&:last-of-type': {
    borderBottom: 'none',
  },

  // eslint-disable-next-line sort-keys, @typescript-eslint/naming-convention
  '&:hover': {
    backgroundColor: theme.semanticColors.global_search_results_hover,
  },
}));

const GlobalSearchResults: FC<Props> = ({
  data,
  formElement,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!formElement || data === null) {
    return null;
  }

  return (
    <GlobalSearchDropdown
      anchorEl={formElement}
      isOpen={isOpen}
      offset={10}
      onClose={onClose}
    >
      <Fade
        isIn={isOpen}
        transitionSpeedInMs={globalSearchTransitionSpeedMs}
      >
        <Box
          borderColor="card_border"
          borderRadius={8}
          overflow="hidden"
        >
          {data.length === 0
            ? (
              <Box
                backgroundColor="card_background"
                maxWidth={globalSearchInputMaxWidth}
                paddingX={20}
                paddingY={10}
              >
                <Heading4
                  alignment="center"
                  fontWeight="semiBold"
                >
                  {t('common:globalSearch.noResultsFound', 'No results found')}
                </Heading4>

                <SmallBody
                  alignment="center"
                  marginBottom={3}
                >
                  {t('common:globalSearch.checkDetails', 'Check details and try again')}
                </SmallBody>

                <Legal alignment="center">
                  {/* eslint-disable-next-line @stylistic/max-len */}
                  {t('common:globalSearch.note', 'Note: Invoices and Payments results show only for Payors you are connected to')}
                </Legal>
              </Box>
            )
            : (
              <ResultsContainer>
                {data.map(section => (
                  <Box key={section.id}>
                    <LabelContainer className="label-container">
                      <Heading5
                        as="span"
                        fontColor="global_search_results_heading"
                        fontWeight="semiBold"
                      >
                        {section.label}
                      </Heading5>

                      {section.to && (
                        <RouterLink
                          fontSize="smallBody"
                          onBeforeNavigate={onClose}
                          to={section.to}
                        >
                          View all
                        </RouterLink>
                      )}
                    </LabelContainer>

                    {section.data.map(result => (
                      <LinkSection
                        fullWidth
                        key={result.id}
                        onBeforeNavigate={onClose}
                        to={result.to}
                      >
                        <Box
                          alignItems="center"
                          display="flex"
                          justifyContent="space-between"
                          marginBottom={1}
                          truncateWidth="100%"
                        >
                          <SmallBody>
                            {result.topLeft}
                          </SmallBody>

                          <SmallBody
                            fontWeight="semiBold"
                            {...['string', 'number'].includes(typeof result.topRight) ? { as: 'span' } : {}}
                          >
                            {result.topRight}
                          </SmallBody>
                        </Box>

                        <Box
                          alignItems="center"
                          display="flex"
                          justifyContent="space-between"
                        >
                          <Legal
                            fontColor="global_search_results_secondary_text"
                            truncateWidth="100%"
                          >
                            {result.bottomLeft}
                          </Legal>

                          <Pill {...result.pillProps} />
                        </Box>
                      </LinkSection>
                    ))}
                  </Box>
                ))}
              </ResultsContainer>
            )}
        </Box>
      </Fade>
    </GlobalSearchDropdown>
  );
};

export default GlobalSearchResults;
